import React, { useEffect, useMemo, useRef } from 'react'
import { Divider, Grid, Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import { useRouter } from '@src/routes'
import { Skeleton } from '@material-ui/lab'
import { useMilliplexKitsLazyQuery } from '@src/queries/MilliplexKitsQuery.generated'
import ProductCard from './ProductCard'
import ResponsivePagination from '@src/components/ResponsivePagination'
import { ListKits, InstrumentTypeItem } from '@src/types/graphql-types'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../Milliplex'
import { isValidArray, PlateTypeWithStatus } from '../../utils'
import AdvancedSearchCriteria from '../AdvancedSearchCriteria'
import { getDefaultOrConvertToArray } from '@src/routes/Configurators/utils/util'

const useStyles = makeStyles((theme: Theme) => ({
  searchResultHeading: {
    marginTop: theme.spacing(6),
  },
  searchResultText: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  productSkeleton: {
    minWidth: '100%',
    height: theme.typography.pxToRem(270),
    display: 'block',
    border: 'solid 1px',
    borderColor: theme.palette.grey[500],
    borderRadius: '5px',
    marginLeft: 0,
    marginRight: 0,
  },
  skeletonCardContainer: {
    height: theme.typography.pxToRem(200),
  },
  skeletonContainer: {
    marginBottom: theme.spacing(6),
  },
  paginationContainer: {
    marginTop: theme.spacing(6),
  },
  previouslyViewed: {
    marginBottom: theme.spacing(12),
  },
  italicText: {
    fontStyle: 'italic',
    marginTop: theme.spacing(4),
  },
  analytesUnderline: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: -5,
      height: '3px',
      width: '100%',
      backgroundColor: theme.palette.warning.main,
      borderRadius: '10px',
    },
  },
  selectKitsDivider: {
    marginBottom: theme.spacing(6),
  },
}))

export interface SearchResultProps {
  previouslyViewedKit: ListKits | null | undefined
  displayPageError: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
  plateType: PlateTypeWithStatus[]
  instrumentType: InstrumentTypeItem[]
}

const SearchResult: React.FC<SearchResultProps> = ({
  previouslyViewedKit,
  displayPageError,
  setDisplayPageError,
  plateType,
  instrumentType,
}) => {
  const classes = useStyles()
  const [currentPage, setCurrentPage] = React.useState(1)
  const { query, replace, pathname } = useRouter()
  const formik = useFormikContext<MilliplexFormValues>()
  const itemsPerPage = 10
  const [isPaginationClick, setIsPaginationClick] = React.useState(false)
  const productCartRef = useRef<HTMLDivElement>(null)

  const analytes = getDefaultOrConvertToArray(query?.analytes)

  const species = previouslyViewedKit?.species || query.species

  const [fetchMilliplexKits, { loading, data }] = useMilliplexKitsLazyQuery()
  const memoizedAnalytes = useMemo(
    () => formik.values.analytes,
    [formik.values.analytes]
  )
  const plateTypeValue = formik.values.plateType
  const instrumentTypeValue = formik.values.instrumentType
  const handlePaginationClick = (currentPage) => {
    setIsPaginationClick(true)
    setCurrentPage(currentPage)
  }
  useEffect(() => {
    plateTypeValue &&
      isValidArray(instrumentTypeValue) &&
      isValidArray(memoizedAnalytes) &&
      fetchMilliplexKits({
        variables: {
          input: {
            page: currentPage,
            species: species,
            researchAreas: getDefaultOrConvertToArray(query?.researchAreas),
            selectedAssayType: formik.values.assay,
            plateType: formik.values.plateType,
            instrumentTypes: formik.values.instrumentType,
            analytes: formik.values.analytes,
            count: itemsPerPage,
            sort: 'analytesbestmatch',
            previouslyViewedPanelId: previouslyViewedKit?.panelId,
          },
        },
      })
    if (isPaginationClick) {
      window.scrollTo({
        top: productCartRef.current?.offsetTop,
        behavior: 'smooth',
      })
      setIsPaginationClick(false)
    }
  }, [
    plateTypeValue,
    instrumentTypeValue,
    memoizedAnalytes,
    currentPage,
    previouslyViewedKit,
  ])

  useEffect(() => {
    if (previouslyViewedKit) {
      previouslyViewedKit?.species &&
        formik.setFieldValue('species', previouslyViewedKit?.species)
      previouslyViewedKit?.researchAreas &&
        formik.setFieldValue(
          'researchAreas',
          previouslyViewedKit?.researchAreas
        )
      previouslyViewedKit?.assayTypeSelection &&
        formik.setFieldValue('assay', previouslyViewedKit?.assayTypeSelection)

      previouslyViewedKit?.plateType &&
        formik.setFieldValue('plateType', previouslyViewedKit?.plateType)
      //update URL with this values
      replace(
        {
          pathname: pathname,
          query: {
            ...query,
            species: previouslyViewedKit?.species,
            researchAreas: previouslyViewedKit?.researchAreas as string[],
            assay: previouslyViewedKit?.assayTypeSelection,
            plateType: previouslyViewedKit.plateType,
          },
        },
        undefined,
        { shallow: true, scroll: false }
      )
    }
  }, [previouslyViewedKit])

  const start = (currentPage - 1) * itemsPerPage + 1
  const end = Math.min(
    start + itemsPerPage - 1,
    data?.getMilliplexKits?.count || 0
  )
  const totalCount = data?.getMilliplexKits?.count || 0
  const numberOfPages = Math.ceil((totalCount || 10) / 10)
  const displaySearchResult =
    !loading &&
    data?.getMilliplexKits?.listKits &&
    data?.getMilliplexKits?.listKits?.length > 0 &&
    query?.analytes?.length &&
    query?.analytes?.length > 0 &&
    formik.values.instrumentType.length > 0
  const noResults =
    !loading &&
    query?.analytes?.length &&
    query?.analytes?.length > 0 &&
    formik.values.instrumentType.length > 0 &&
    data?.getMilliplexKits?.listKits &&
    data?.getMilliplexKits?.listKits?.length === 0

  useEffect(() => {
    if (currentPage > numberOfPages && totalCount !== 0) {
      setCurrentPage(numberOfPages)
    }
  }, [analytes])

  return (
    <>
      {previouslyViewedKit && (
        <div className={classes.previouslyViewed}>
          <Typography variant="h3" className={classes.searchResultHeading}>
            <FormattedMessage
              id="PREVIOUSLY_VIEWED_PRODUCT"
              defaultMessage="Previously Viewed Product"
            />
          </Typography>
          <ProductCard milliplexKit={previouslyViewedKit} analytes={analytes} />
        </div>
      )}
      <AdvancedSearchCriteria
        displayPageError={displayPageError}
        setDisplayPageError={setDisplayPageError}
        plateType={plateType}
        instrumentType={instrumentType}
      />
      {displaySearchResult && (
        <>
          <Divider className={classes.selectKitsDivider} />
          <Typography variant="h2">
            <FormattedMessage
              id="SELECT_MILLIPLEX_KITS"
              defaultMessage="Select Milliplex Kits"
            />
          </Typography>
        </>
      )}

      {previouslyViewedKit && ({ displaySearchResult } || noResults) ? (
        <Typography variant="h3" className={classes.searchResultHeading}>
          <FormattedMessage
            id="YOU_MAY_ALSO_LIKE"
            defaultMessage="You May Also Like"
          />
        </Typography>
      ) : (
        (displaySearchResult || noResults) && (
          <Typography
            ref={productCartRef}
            variant="h3"
            className={classes.searchResultHeading}
          >
            <FormattedMessage
              id="SEARCH_RESULTS"
              defaultMessage="Search Results"
            />
          </Typography>
        )
      )}
      {loading && (
        <Grid container className={classes.skeletonContainer}>
          {Array(4)
            .fill(undefined)
            .map((_, index) => (
              <Grid
                key={index}
                xs={12}
                item
                className={classes.skeletonCardContainer}
              >
                <Skeleton
                  animation="wave"
                  className={classes.productSkeleton}
                />
              </Grid>
            ))}
        </Grid>
      )}
      {displaySearchResult && (
        <>
          <p className={classes.searchResultText}>
            {`Showing ${start} - ${end} of ${totalCount} results.`}{' '}
            <Typography variant="inherit">
              <FormattedMessage id="MATCHING" defaultMessage="Matching" />{' '}
              <span className={classes.analytesUnderline}>
                <FormattedMessage id="ANALYTES" defaultMessage="analytes" />
              </span>{' '}
              <FormattedMessage
                id="ARE_HIGHLIGHTED"
                defaultMessage="are highlighted."
              />
            </Typography>
          </p>
          {data?.getMilliplexKits?.listKits.map(
            (milliplexKit, index) =>
              milliplexKit.panelId !== query.previouslyViewed && (
                <ProductCard
                  key={index}
                  milliplexKit={milliplexKit}
                  analytes={analytes}
                />
              )
          )}
          <div className={classes.paginationContainer}>
            <ResponsivePagination
              loading={loading}
              numPages={numberOfPages}
              currentPage={currentPage}
              handleUpdatePage={handlePaginationClick}
            />
          </div>
        </>
      )}
      {noResults && (
        <p className={classes.searchResultText}>
          <FormattedMessage
            id="NO_RESULTS_FOUND"
            defaultMessage="No results found"
          />
        </p>
      )}
    </>
  )
}

export default SearchResult
